/**
 * Whether the player should tell the backend the trainer saw a clip, frame by frame.
 *
 * Seen is posted on the first frame, once per clip per opening, as on mobile. A post that fails is
 * tried again on a later frame, no sooner than `SEEN_RETRY_MS` after the failure, and the player
 * stops after `SEEN_ATTEMPTS` so a backend that refuses does not hear from every frame of a loop.
 */

export type SeenMark =
  | { readonly kind: 'sending' }
  | { readonly kind: 'done' }
  | { readonly kind: 'failed'; readonly attempts: number; readonly at: number };

const SEEN_RETRY_MS = 5_000;
const SEEN_ATTEMPTS = 3;

/** On a presented frame: post now, or not. `undefined` is a clip this opening has not posted yet. */
export function seenIsDue(mark: SeenMark | undefined, now: number): boolean {
  if (mark === undefined) return true;
  if (mark.kind !== 'failed') return false;
  return mark.attempts < SEEN_ATTEMPTS && now - mark.at >= SEEN_RETRY_MS;
}

/** The mark after a post failed at `now`. */
export function seenFailed(mark: SeenMark | undefined, now: number): SeenMark {
  const attempts = mark?.kind === 'failed' ? mark.attempts + 1 : 1;
  return { kind: 'failed', attempts, at: now };
}

/** The player has stopped trying, and says so once. */
export function seenGaveUp(mark: SeenMark | undefined): boolean {
  return mark?.kind === 'failed' && mark.attempts >= SEEN_ATTEMPTS;
}
